import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { ClickAwayListener } from '@mui/material';

import ColorCheckboxRow from './ColorCheckboxRow/ColorCheckboxRow';
import Link from 'components/UI/Link/Link';
import FilterPopper from 'components/Listing/Filters/FilterPopper/FilterPopper';
import MoreFiltersButton from 'components/Listing/Filters/FiltersDesktop/MoreFiltersButton/MoreFiltersButton';
import CheckboxRow from 'components/UI/Form/Row/CheckboxRow/CheckboxRow';
import { getUri } from 'services/generic';
import {
  FilterType,
  FilterValueType,
} from 'types/ProductListing/ProductListing';

import styles from './FiltersDesktop.module.scss';

type PropsType = HTMLAttributes<HTMLDivElement> & {
  filters: FilterType[];
  onFilterClick: (filter: FilterType, filterValue: FilterValueType) => void;
  isSticky: boolean;
};

const CHECKBOX_COMPONENTS = {
  Couleur: ColorCheckboxRow,
  Colour: ColorCheckboxRow,
};

export default function FiltersDesktop({
  filters,
  onFilterClick,
  isSticky,
  className,
  ...props
}: PropsType): JSX.Element {
  const [displayExpandButton, setDisplayExpandButton] = useState(false);
  const [openedFilter, setOpenedFilter] = useState<string>();
  const [expandedFilters, setExpandedFilters] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calculateCollapse = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const clone = content.cloneNode(true) as HTMLElement;

    clone.style.height = '0';
    clone.style.flexWrap = 'nowrap';
    clone.style.whiteSpace = 'nowrap';
    clone.style.marginLeft = '0';

    if (clone.firstChild instanceof HTMLElement) {
      clone.firstChild.style.marginLeft = '0';
    }

    content.after(clone);

    setDisplayExpandButton(
      clone.scrollWidth > container.getBoundingClientRect().width
    );

    clone.remove();
  }, []);

  useEffect(() => {
    calculateCollapse();
    window.addEventListener('resize', calculateCollapse);

    return () => window.removeEventListener('resize', calculateCollapse);
  }, [filters]);

  function handleFilterChange(filterName: string) {
    if (filterName === openedFilter) {
      return setOpenedFilter(undefined);
    }

    setOpenedFilter(filterName);
  }

  function handleResetOpenedFilter(applicantName: string) {
    if (openedFilter === applicantName) {
      setOpenedFilter(undefined);
    }
  }

  function handleDisplayFilters() {
    setExpandedFilters(prev => !prev);
  }

  return (
    <div
      className={classNames(styles.container, className)}
      ref={containerRef}
      {...props}
    >
      <div
        className={classNames(
          styles.filtersWrapper,
          expandedFilters && styles.expanded
        )}
      >
        <div ref={contentRef} className={styles.filters}>
          {filters.map(filter => {
            const { name, label, values, appliedCount } = filter;
            const Component = CHECKBOX_COMPONENTS[label] ?? CheckboxRow;

            return (
              <ClickAwayListener
                key={name}
                onClickAway={() => handleResetOpenedFilter(name)}
              >
                <div className={styles.filter}>
                  <FilterPopper
                    label={label}
                    open={openedFilter === name}
                    onClick={() => handleFilterChange(name)}
                    {...(appliedCount > 0 && { appliedCount: appliedCount })}
                    classes={{ paper: styles.paper }}
                  >
                    <div className={styles.innerContainer}>
                      {values.map(value => {
                        const { isApplied, label: itemLabel, url } = value;
                        return (
                          <Link
                            key={itemLabel}
                            to={getUri(url)}
                            onClick={() => onFilterClick(filter, value)}
                            state={{ scroll: false, scrollToRef: isSticky }}
                            className={styles.link}
                          >
                            <Component
                              label={itemLabel}
                              inputProps={{
                                name: label,
                                checked: isApplied,
                              }}
                              classes={{
                                row: styles.itemRow,
                                label: classNames(
                                  styles.itemLabel,
                                  isApplied && styles.highlighted
                                ),
                                inputContainer: styles.itemInputContainer,
                                inputRoot: styles.itemInputRoot,
                                icon: styles.itemIcon,
                                checkedIcon: styles.itemIconChecked,
                              }}
                            />
                          </Link>
                        );
                      })}
                    </div>
                  </FilterPopper>
                </div>
              </ClickAwayListener>
            );
          })}
        </div>
      </div>
      {displayExpandButton && (
        <MoreFiltersButton
          active={expandedFilters}
          onClick={handleDisplayFilters}
          className={styles.toggleMore}
        />
      )}
    </div>
  );
}
